import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Check,
  Code2,
  CreditCard,
  Database,
  Download,
  ExternalLink,
  FileImage,
  Globe,
  Image,
  Key,
  KeyRound,
  Layers,
  LayoutDashboard,
  Link2,
  LogIn,
  Paintbrush,
  Palette,
  Search,
  Sheet,
  Sparkles,
  Table2,
  Upload,
  Wand2,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import "./SnapLayerFeatureLanding.css";

type IconComponent = LucideIcon;

type FeatureCard = {
  icon: IconComponent;
  title: string;
  status: string;
  description: string;
  details: string[];
};

type IntegrationCard = {
  icon: IconComponent;
  title: string;
  label: string;
  description: string;
  tools: string[];
};

type PricingPlan = {
  name: string;
  monthly: number;
  annual: number;
  description: string;
  highlight?: boolean;
  features: string[];
};

type RocketSmokeStyle = React.CSSProperties & Record<"--smoke-x" | "--smoke-y" | "--smoke-scale" | "--smoke-delay", string>;

const NAV_LINKS = [
  { href: "#features", label: "Features" },
  { href: "#workflow", label: "Workflow" },
  { href: "#integrations", label: "Integrations" },
  { href: "#pricing", label: "Pricing" },
];

const HERO_STATS = [
  ["PSDs", "start with designs you already love"],
  ["Sheets", "make every row feel personal"],
  ["Links", "send one-of-a-kind visuals"],
  ["BYOK", "bring the AI tools you prefer"],
];

const CORE_FEATURES: FeatureCard[] = [
  {
    icon: Table2,
    title: "Turn a sheet into a campaign",
    status: "Live",
    description:
      "Add names, companies, offers, links, colors, screenshots, and logos once, then let SnapSheet make every image feel like it was built by hand.",
    details: ["CSV and row editing", "Logo and image columns", "Preview every person"],
  },
  {
    icon: FileImage,
    title: "Start with your PSDs",
    status: "Live",
    description:
      "Bring in the Photoshop files your team already made, keep the layers editable, and turn those designs into reusable campaign templates.",
    details: ["Layered PSD import", "Editable text and images", "No rebuild from scratch"],
  },
  {
    icon: Layers,
    title: "Make every layer personal",
    status: "Live",
    description:
      "Swap logos, tune brand colors, move elements, change headlines, style text, and control the tiny details that make the image look intentional.",
    details: ["Tint and shadows", "Text styling", "Precise placement"],
  },
  {
    icon: BarChart3,
    title: "Add data when it helps the story",
    status: "Live",
    description:
      "Use LayerSheets to add simple charts, scorecards, comparisons, and proof points to an image without turning the campaign into a full report.",
    details: ["Charts and scorecards", "Grouped data", "Clear visual proof"],
  },
  {
    icon: Link2,
    title: "Send a personal link for every person",
    status: "Live",
    description:
      "Create SnapLinks and SnapURLs for outreach, landing pages, email images, sales follow-up, or any place a personalized visual can earn attention.",
    details: ["SnapLinks", "SnapURL Builder", "Share-ready images"],
  },
  {
    icon: LayoutDashboard,
    title: "SnapPages",
    status: "Live",
    description:
      "Give each prospect or client a simple branded page with their personalized image, so the experience feels more like a concierge moment than a file attachment.",
    details: ["Personalized pages", "Branded delivery", "Follow-up ready"],
  },
  {
    icon: Workflow,
    title: "Collect campaign inputs cleanly",
    status: "Live",
    description:
      "Use API Builder pages when clients, partners, or teammates need to submit campaign details without touching the full editor.",
    details: ["Simple intake pages", "Password-gated", "Team-friendly"],
  },
  {
    icon: Code2,
    title: "Connect the busywork later",
    status: "Live",
    description:
      "When you are ready, SnapLayer can fit into the tools your team already uses, so personalized images can be created automatically behind the scenes.",
    details: ["Automation-ready", "CRM-friendly", "Developer optional"],
  },
];

const CONTROL_POINTS = [
  { label: "Brand color", value: "per company", icon: Palette },
  { label: "Logo size", value: "just right", icon: Image },
  { label: "Headline style", value: "on brand", icon: Paintbrush },
  { label: "Proof point", value: "LayerSheet", icon: BarChart3 },
  { label: "Link preview", value: "1200 x 630", icon: Download },
];

const WORKFLOW_STEPS = [
  {
    title: "Choose a design that already looks like you",
    description:
      "Start with a PSD, a Canva asset, an uploaded design, or a SnapLayer template that matches your brand and your campaign goal.",
  },
  {
    title: "Tell SnapLayer what should change",
    description:
      "Mark the parts that should become personal: the name, company, logo, offer, website image, color, chart, CTA, or message.",
  },
  {
    title: "Bring in the details",
    description:
      "Use SnapSheet, Google Sheets, CSVs, logo lookups, website screenshots, TexAu, PhantomBuster, or AI help to fill in the campaign data.",
  },
  {
    title: "Launch everywhere",
    description:
      "Export the images, send SnapLinks, publish SnapPages, build SnapURLs, or connect the workflow to the places your team already works.",
  },
];

const INTEGRATIONS: IntegrationCard[] = [
  {
    icon: Search,
    title: "Brand and logo lookup",
    label: "Live",
    description:
      "Find the right logo from a company domain and drop it into the image without chasing brand folders or asking prospects for files.",
    tools: ["Brandfetch", "Logo.dev", "Extract.pics"],
  },
  {
    icon: Globe,
    title: "Website screenshots",
    label: "Live",
    description:
      "Show someone their own website inside the creative so the image feels instantly specific to them.",
    tools: ["thum.io", "Stored image assets", "Website layers"],
  },
  {
    icon: Sheet,
    title: "Spreadsheet data",
    label: "Rolling out",
    description:
      "Build campaigns from rows your team can understand, then connect Google Sheets as the approval and launch work wraps up.",
    tools: ["SnapSheet", "Google Sheets", "CSV"],
  },
  {
    icon: Upload,
    title: "Creative imports",
    label: "Live and gated",
    description:
      "Reuse the creative work you already have. PSD import is live, and Canva is moving through its gated launch path.",
    tools: ["PSD", "Canva", "Template gallery"],
  },
  {
    icon: Sparkles,
    title: "AI model providers",
    label: "BYOK",
    description:
      "Ask AI for campaign angles, row copy, image prompts, and cleanup help while keeping the freedom to bring your own provider keys.",
    tools: ["OpenAI", "OpenRouter", "Straico", "Qolaba"],
  },
  {
    icon: Database,
    title: "Prospect and report inputs",
    label: "Workflow-ready",
    description:
      "Turn enrichment exports into useful image ingredients: scores, snippets, logos, screenshots, comparisons, and quick proof points.",
    tools: ["TexAu", "PhantomBuster", "DataForSEO", "GA4"],
  },
];

const AI_WORKFLOWS = [
  "Turn a rough campaign idea into headline options, CTAs, and image directions.",
  "Fill blank rows with short, useful copy that still sounds human.",
  "Clean up TexAu or PhantomBuster exports so they are ready for personalized images.",
  "Use your own AI keys when you want model choice, control, and predictable costs.",
];

const PRICING_PLANS: PricingPlan[] = [
  {
    name: "Solo",
    monthly: 29,
    annual: 23,
    description: "For solo creators testing personalized images in real campaigns.",
    features: [
      "10 templates",
      "1 GB storage",
      "50 SnapSheet rows",
      "5 text fields per template",
      "3 image fields per template",
      "Limited community access",
      "BYOK AI connections",
    ],
  },
  {
    name: "Plus",
    monthly: 79,
    annual: 63,
    description: "For small teams sending polished visuals to prospects and clients.",
    features: [
      "25 templates",
      "5 GB storage",
      "10 SnapLinks",
      "100 SnapSheet rows",
      "3 SnapPages",
      "PSD import and export",
      "Enhanced community access",
    ],
  },
  {
    name: "Pro",
    monthly: 149,
    annual: 119,
    description: "For agencies building repeatable campaigns across clients and accounts.",
    highlight: true,
    features: [
      "50 templates",
      "20 GB storage",
      "25 SnapLinks",
      "250 SnapSheet rows",
      "10 SnapPages",
      "3 team members",
      "25 sub accounts",
      "PSD import and export",
      "Full community access",
    ],
  },
  {
    name: "Agency",
    monthly: 249,
    annual: 199,
    description: "For high-volume teams turning lists, brands, and offers into visual campaigns.",
    features: [
      "100 templates",
      "100 GB storage",
      "50 SnapLinks",
      "1,000 SnapSheet rows",
      "25 SnapPages",
      "10 team members",
      "50 sub accounts",
      "50 text and 30 image fields",
      "Full community access",
    ],
  },
];

const CODE_LINES = [
  "SnapURL Builder: make the exact image link you need",
  "SnapLinks: one personal visual for each person",
  "SnapPages: a branded place to send each prospect",
  "API Builder: simple intake pages for partners and teams",
];

const ROCKET_SMOKE: RocketSmokeStyle[] = Array.from({ length: 64 }, (_, index) => {
  const column = index % 16;
  const row = Math.floor(index / 16);
  const side = column - 7.5;
  const spread = 8 + row * 16;
  const smokeX = `${side * spread}px`;
  const smokeY = `${32 + row * 22 + (column % 3) * 8}px`;
  const smokeScale = `${0.55 + ((index * 7) % 12) / 10}`;
  const smokeDelay = `${(index % 13) * 0.09}s`;
  return {
    "--smoke-x": smokeX,
    "--smoke-y": smokeY,
    "--smoke-scale": smokeScale,
    "--smoke-delay": smokeDelay,
  };
});

const FIRE_PLUMES = ["left-wide", "left", "center", "right", "right-wide", "core"];

const RocketLaunchScene: React.FC = () => {
  const [launched, setLaunched] = useState(false);

  useEffect(() => {
    const launchTimer = window.setTimeout(() => setLaunched(true), 700);
    return () => window.clearTimeout(launchTimer);
  }, []);

  return (
    <div className={`snap-v2-rocket-stage ${launched ? "is-launched" : ""}`} aria-hidden="true">
      <div className="snap-v2-rocket-copy">
        <span>Launch a campaign</span>
        <strong>Leave the heavy lifting on the pad.</strong>
      </div>
      <div className="snap-v2-rocket-field">
        <img src="/assets/rocket/TowerPad_512.png" alt="" className="snap-v2-rocket-tower" />
        <div className="snap-v2-rocket-ship">
          <img src="/assets/rocket/Rocket_512.png" alt="" className="snap-v2-rocket-body" />
          <img src="/assets/rocket/Flame_512.png" alt="" className="snap-v2-rocket-flame-asset" />
          <div className="snap-v2-fire-plumes">
            {FIRE_PLUMES.map((plume) => <span key={plume} data-plume={plume} />)}
          </div>
        </div>
        <img src="/assets/rocket/Smoke_512.png" alt="" className="snap-v2-rocket-smoke-asset" />
        <div className="snap-v2-smoke-field">
          {ROCKET_SMOKE.map((style, index) => <span key={index} className="snap-v2-smoke-puff" style={style} />)}
        </div>
        <div className="snap-v2-launch-embers">
          {FIRE_PLUMES.map((plume) => <span key={plume} data-plume={plume} />)}
        </div>
      </div>
    </div>
  );
};

const SnapLayerFeatureLanding: React.FC = () => {
  const [annualBilling, setAnnualBilling] = useState(true);

  return (
    <main className="snap-v2">
      <nav className="snap-v2-nav" aria-label="SnapLayer marketing navigation">
        <a href="#top" className="snap-v2-brand" aria-label="SnapLayer home">
          <img src="/brand/snaplayer-wordmark-transparent.png" alt="SnapLayer" />
        </a>
        <div className="snap-v2-nav-links">
          {NAV_LINKS.map((link) => (
            <a key={link.href} href={link.href}>{link.label}</a>
          ))}
        </div>
        <div className="snap-v2-nav-actions">
          <Link to="/activation" className="snap-v2-ghost-button">
            <KeyRound aria-hidden="true" />
            Activate
          </Link>
          <Link to="/login" className="snap-v2-solid-button">
            <LogIn aria-hidden="true" />
            Log in
          </Link>
        </div>
      </nav>

      <section id="top" className="snap-v2-hero" aria-labelledby="snap-v2-hero-title">
        <div className="snap-v2-hero-scene" aria-hidden="true">
          <RocketLaunchScene />
        </div>

        <div className="snap-v2-hero-content">
          <span className="snap-v2-eyebrow">Personalized images for real campaigns</span>
          <h1 id="snap-v2-hero-title">SnapLayer</h1>
          <p>
            Create images that look made for one person at a time. Start with your designs,
            add a sheet of names, logos, screenshots, offers, and brand colors, then launch
            polished visuals your prospects and clients actually recognize.
          </p>
          <div className="snap-v2-hero-buttons">
            <Link to="/login" className="snap-v2-primary-cta">
              Open SnapLayer
              <ArrowRight aria-hidden="true" />
            </Link>
            <a href="#pricing" className="snap-v2-secondary-cta">
              See pricing
            </a>
          </div>
        </div>

        <div className="snap-v2-hero-stats" aria-label="SnapLayer capabilities">
          {HERO_STATS.map(([value, label]) => (
            <div key={value}>
              <strong>{value}</strong>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </section>

      <section id="features" className="snap-v2-section snap-v2-section-white">
        <div className="snap-v2-section-heading">
          <span className="snap-v2-eyebrow">What you can use now</span>
          <h2>Personalized images without rebuilding the same design all day.</h2>
          <p>
            SnapLayer helps marketers, agencies, and operators turn one strong design
            into a campaign full of personal, on-brand images.
          </p>
        </div>

        <div className="snap-v2-feature-grid">
          {CORE_FEATURES.map((feature) => (
            <article key={feature.title} className="snap-v2-feature-card">
              <div className="snap-v2-card-topline">
                <feature.icon aria-hidden="true" />
                <span>{feature.status}</span>
              </div>
              <div className="snap-v2-feature-visual" aria-hidden="true">
                <feature.icon />
                <span />
                <span />
                <span />
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
              <div className="snap-v2-detail-list">
                {feature.details.map((detail) => <span key={detail}>{detail}</span>)}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="snap-v2-section snap-v2-control-band">
        <div className="snap-v2-control-copy">
          <span className="snap-v2-eyebrow">Fine-grained control</span>
          <h2>Make the image feel personal, not mail-merged.</h2>
          <p>
            The difference is in the details: the logo fits, the colors match, the headline
            sounds right, and the screenshot or proof point belongs to the person seeing it.
          </p>
        </div>

        <div className="snap-v2-control-panel" aria-label="Example personalized controls">
          {CONTROL_POINTS.map((item) => (
            <div key={item.label} className="snap-v2-control-row">
              <item.icon aria-hidden="true" />
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          ))}
        </div>
      </section>

      <section id="workflow" className="snap-v2-section snap-v2-section-warm">
        <div className="snap-v2-section-heading snap-v2-left-heading">
          <span className="snap-v2-eyebrow">How teams use it</span>
          <h2>From campaign idea to personalized visual in four steps.</h2>
        </div>

        <div className="snap-v2-workflow-grid">
          {WORKFLOW_STEPS.map((step, index) => (
            <article key={step.title} className="snap-v2-workflow-step">
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="integrations" className="snap-v2-section snap-v2-section-white">
        <div className="snap-v2-section-heading">
          <span className="snap-v2-eyebrow">Integrations</span>
          <h2>Pull in the pieces that make each image feel specific.</h2>
          <p>
            Logos, website screenshots, spreadsheets, enrichment exports, AI help,
            and creative imports can all become ingredients in the final visual.
          </p>
        </div>

        <div className="snap-v2-integration-grid">
          {INTEGRATIONS.map((integration) => (
            <article key={integration.title} className="snap-v2-integration-card">
              <div className="snap-v2-card-topline">
                <integration.icon aria-hidden="true" />
                <span>{integration.label}</span>
              </div>
              <h3>{integration.title}</h3>
              <p>{integration.description}</p>
              <div className="snap-v2-tool-row">
                {integration.tools.map((tool) => <span key={tool}>{tool}</span>)}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="snap-v2-section snap-v2-ai-band">
        <div className="snap-v2-ai-copy">
          <span className="snap-v2-eyebrow">AI without lock-in</span>
          <h2>Use AI for the blank-page moments, not for lock-in.</h2>
          <p>
            AI can help with campaign angles, copy, row cleanup, and image ideas. Bring
            your own provider keys so usage stays billed through the AI accounts you control.
          </p>
        </div>
        <div className="snap-v2-ai-list">
          {AI_WORKFLOWS.map((workflow) => (
            <div key={workflow}>
              <Wand2 aria-hidden="true" />
              <span>{workflow}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="snap-v2-section snap-v2-api-band">
        <div className="snap-v2-api-copy">
          <span className="snap-v2-eyebrow">API and builder paths</span>
          <h2>Keep the workflow simple now, automate it when you are ready.</h2>
          <p>
            Most teams can launch from the editor, sheets, SnapLinks, SnapPages, and
            SnapURL Builder. When the campaign grows, API Builder and automation paths
            can take the repetitive work off your hands.
          </p>
          <div className="snap-v2-api-actions">
            <Link to="/docs/api-reference">
              API reference
              <ExternalLink aria-hidden="true" />
            </Link>
            <Link to="/docs/api-builder">
              API Builder docs
              <ExternalLink aria-hidden="true" />
            </Link>
          </div>
        </div>
        <div className="snap-v2-code-panel" aria-label="Example API paths">
          {CODE_LINES.map((line) => <code key={line}>{line}</code>)}
        </div>
      </section>

      <section id="pricing" className="snap-v2-section snap-v2-section-warm">
        <div className="snap-v2-section-heading">
          <span className="snap-v2-eyebrow">Pricing</span>
          <h2>Simple monthly and annual tiers.</h2>
          <p>
            Start small, then grow into more templates, rows, pages, storage, and team access
            as personalized images become part of your regular campaign rhythm.
          </p>
          <div className="snap-v2-billing-toggle" aria-label="Billing period">
            <button
              type="button"
              data-active={!annualBilling}
              onClick={() => setAnnualBilling(false)}
            >
              Monthly
            </button>
            <button
              type="button"
              data-active={annualBilling}
              onClick={() => setAnnualBilling(true)}
            >
              Annual
              <span>2 months free</span>
            </button>
          </div>
        </div>

        <div className="snap-v2-pricing-grid">
          {PRICING_PLANS.map((plan) => {
            const price = annualBilling ? plan.annual : plan.monthly;
            return (
              <article
                key={plan.name}
                className="snap-v2-pricing-card"
                data-highlight={plan.highlight ? "true" : "false"}
              >
                <div className="snap-v2-price-heading">
                  <h3>{plan.name}</h3>
                  {plan.highlight && <span>Best fit</span>}
                </div>
                <p>{plan.description}</p>
                <div className="snap-v2-price">
                  <strong>${price}</strong>
                  <span>/mo</span>
                </div>
                {annualBilling && <div className="snap-v2-price-note">Billed annually</div>}
                <ul>
                  {plan.features.map((feature) => (
                    <li key={feature}>
                      <Check aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <a href="https://getsnaplayer.com" target="_blank" rel="noreferrer">
                  Get this plan
                  <ArrowRight aria-hidden="true" />
                </a>
              </article>
            );
          })}
        </div>

        <div className="snap-v2-ai-pricing-note">
          <CreditCard aria-hidden="true" />
          <span>
            AI usage is billed by your connected provider accounts. SnapLayer does not
            sell or maintain a separate AI credit balance.
          </span>
        </div>
      </section>

      <section className="snap-v2-final-cta">
        <div>
          <img src="/brand/snaplayer-mark-trimmed-w-buffer.png" alt="" />
          <span className="snap-v2-eyebrow">Ready for review</span>
          <h2>Ready to launch the new SnapLayer story.</h2>
          <p>
            The classic page is still available from the toggle above while you compare
            this more human, campaign-focused direction.
          </p>
          <div className="snap-v2-hero-buttons">
            <Link to="/login" className="snap-v2-primary-cta">
              Log in
              <LogIn aria-hidden="true" />
            </Link>
            <Link to="/activation" className="snap-v2-secondary-cta">
              Activate license
              <Key aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="snap-v2-footer">
        <span>SnapLayer</span>
        <div>
          <a href="mailto:support@getsnaplayer.com">support@getsnaplayer.com</a>
          <a href="https://getsnaplayer.com" target="_blank" rel="noreferrer">GetSnapLayer.com</a>
        </div>
      </footer>
    </main>
  );
};

export default SnapLayerFeatureLanding;
